import { useEffect, useMemo, useState } from 'react';
import { format, isValid, parseISO, parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { School } from '../types/types';

/**
 * Date format constants for consistent formatting across the application
 *
 * Usage examples:
 * - For attendance displays: DATE_FORMATS.ATTENDANCE_DATE
 * - For reports: DATE_FORMATS.REPORT_DATE
 * - For session displays: DATE_FORMATS.SESSION_DATE
 * - For datetime with time: DATE_FORMATS.DATETIME_SHORT
 */
export const DATE_FORMATS = {
  // Date only formats
  SHORT_DATE: 'MMM dd, yyyy', // Jan 15, 2024
  LONG_DATE: 'MMMM dd, yyyy', // January 15, 2024
  NUMERIC_DATE: 'MM/dd/yyyy', // 01/15/2024
  ISO_DATE: 'yyyy-MM-dd', // 2024-01-15
  DISPLAY_DATE: 'dd MMM yyyy', // 15 Jan 2024
  COMPACT_DATE: 'dd/MM/yy', // 15/01/24

  // Time only formats
  TIME_12H: 'h:mm a', // 2:30 PM
  TIME_24H: 'HH:mm', // 14:30
  TIME_WITH_SECONDS: 'HH:mm:ss', // 14:30:45

  // Date and time formats
  DATETIME_SHORT: 'MMM dd, yyyy h:mm a', // Jan 15, 2024 2:30 PM
  DATETIME_LONG: 'MMMM dd, yyyy h:mm a', // January 15, 2024 2:30 PM
  DATETIME_FULL: 'EEEE, MMMM dd, yyyy h:mm a', // Monday, January 15, 2024 2:30 PM

  // Common use case formats
  ATTENDANCE_DATE: 'MMM dd, yyyy', // For attendance history dates
  SESSION_DATE: 'MMM yyyy', // For session display (Jan 2024)
  REPORT_DATE: 'dd/MM/yyyy', // For reports
  LOG_TIMESTAMP: 'yyyy-MM-dd HH:mm:ss', // For logging
} as const;

/**
 * True inside the Skuulbe parent app shell. The `X-APP: parent` header is only
 * sent on the shell's first request, so after a full-page navigation we also
 * rely on signals that survive it: the WebView user-agent suffix and the
 * injected `ReactNativeWebView` bridge.
 */
const detectNativeShell = (xApp: unknown): boolean =>
  xApp === 'parent' ||
  typeof window.ReactNativeWebView !== 'undefined' ||
  /\bSkuulbeShell\/1\b/.test(navigator.userAgent);

/**
 * Hook for accessing school instance data and timezone-aware date utilities
 *
 * @example
 * ```tsx
 * const { instance, dateUtils, DATE_FORMATS } = useInstance();
 *
 * // Format a date using predefined constants
 * const formattedDate = dateUtils.formatDate('2024-01-15', DATE_FORMATS.ATTENDANCE_DATE);
 *
 * // Or use default format (DATE_FORMATS.SHORT_DATE)
 * const defaultFormat = dateUtils.formatDate('2024-01-15');
 *
 * // Get current school time
 * const schoolTime = dateUtils.getSchoolDate();
 *
 * // Get timezone identifier
 * const timezone = dateUtils.getTimezone(); // 'Africa/Accra'
 * ```
 */
export function useInstance() {
  const [loading, setLoading] = useState(true);
  const [instance, setInstance] = useState<School | null>(null);
  const [isRunningOnMobile, setIsRunningOnMobile] = useState<boolean>(false);

  useEffect(() => {
    const initial = window['initialData'];
    const school = initial?.school;
    setIsRunningOnMobile(detectNativeShell(initial?.x_app));

    setInstance({
      id: school?.id,
      name: school?.name,
      displayName: school?.displayName,
      domainName: school?.domainName,
      currency: school?.currency,
      email: school?.email,
      logo: school?.logo,
      phoneNumber: school?.phoneNumber,
      website: school?.website,
      address: school?.address,
      currentSession: {
        id: school?.currentSession?.id,
        displayName: school?.currentSession?.displayName,
        startDate: school?.currentSession?.startDate,
        endDate: school?.currentSession?.endDate,
      },
      settings: {
        homeBackground: school?.settings?.homeBackground,
        letterHead: school?.settings?.letterHead,
        defaultPaymentMethodId: school?.settings?.defaultPaymentMethodId,
      },
      terminalExamId: school?.terminalExamId,
      timezone: school?.timezone || 'Africa/Accra', // Default to Ghana timezone
      version: initial?.version,
    });
    setLoading(false);
  }, []);


  // Date formatting functions using school timezone
  const dateUtils = useMemo(() => {
    const schoolTimezone = instance?.timezone || 'Africa/Accra';

    return {
      /**
       * Format a date string in the school's timezone
       * @param dateString - ISO date string or Date object
       * @param formatPattern - date-fns format pattern (default: DATE_FORMATS.SHORT_DATE)
       * @returns Formatted date string in school timezone
       */
      formatDate: (
        dateString: string | Date,
        formatPattern: string = DATE_FORMATS.SHORT_DATE
      ): string => {
        if (!dateString) return '';

        try {
          // Handle date-only strings without timezone shifting (e.g. 2024-03-28)
          if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString.trim())) {
            const date = parse(dateString.trim(), 'yyyy-MM-dd', new Date());
            return format(date, formatPattern);
          }

          // If it's a string without timezone info (like from datetime-local input),
          // we treat it as being in the school timezone directly to avoid browser timezone linkage.
          const date =
            typeof dateString === 'string'
              ? (/(Z|[+-]\d{2}:\d{2})$/i.test(dateString.trim())
                  ? parseISO(dateString)
                  : fromZonedTime(dateString, schoolTimezone))
              : dateString;

          return formatInTimeZone(date, schoolTimezone, formatPattern);
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      },

      /**
       * Format a date string with timezone display
       * @param dateString - ISO date string or Date object
       * @param formatPattern - date-fns format pattern (default: DATE_FORMATS.DATETIME_SHORT)
       * @returns Formatted date string with timezone in school timezone
       */
      formatDateWithTimezone: (
        dateString: string | Date,
        formatPattern: string = DATE_FORMATS.DATETIME_SHORT
      ): string => {
        if (!dateString) return '';

        try {
          const date =
            typeof dateString === 'string' ? parseISO(dateString) : dateString;
          return formatInTimeZone(date, schoolTimezone, formatPattern);
        } catch (error) {
          console.error('Error formatting date with timezone:', error);
          return '';
        }
      },

      /**
       * Convert any date (string or object) to a UTC point, 
       * assuming wall-clock strings are in the school's timezone.
       */
      toUTC: (date: string | Date): string => {
        if (!date) return '';
        try {
          if (typeof date === 'string' && !date.includes('Z') && !date.includes('+')) {
            // Treat as wall-clock time in school timezone
            return fromZonedTime(date, schoolTimezone).toISOString();
          }
          const dateObj = typeof date === 'string' ? parseISO(date) : date;
          return dateObj.toISOString();
        } catch {
          return '';
        }
      },

      /**
       * Format date for HTML datetime-local input fields
       * @param dateString - ISO date string or Date object
       * @returns Date formatted as YYYY-MM-DDTHH:mm for input fields
       */
      formatInputDate: (dateString?: string | Date): string => {
        if (dateString === undefined) {
          dateString = new Date();
        }
        if (!dateString) return '';

        try {
          const date =
            typeof dateString === 'string' ? parseISO(dateString) : dateString;
          // If it's already a date object or an ISO string with timezone, convert to school time
          // formatTz expects a UTC date point
          return formatInTimeZone(date, schoolTimezone, "yyyy-MM-dd'T'HH:mm");
        } catch (error) {
          console.error('Error formatting input date:', error);
          return '';
        }
      },

      /**
       * Format date with timezone for display (replaces formatDateToLocal)
       * @param dateString - ISO date string or Date object
       * @returns Date formatted in school timezone with user-friendly format
       */
      formatDateToLocal: (dateString: string | Date): string => {
        if (!dateString) return '';

        try {
          const date =
            typeof dateString === 'string' ? parseISO(dateString) : dateString;
          return formatInTimeZone(date, schoolTimezone, 'MM/dd/yy, h:mm a');
        } catch (error) {
          console.error('Error formatting date to local:', error);
          return '';
        }
      },

      /**
       * Get current date in school timezone
       * @returns Date object in school timezone
       */
      getSchoolDate: (): Date => {
        return toZonedTime(new Date(), schoolTimezone);
      },

      /**
       * Get the hour (0-23) of a date in the school's timezone
       * @param dateString - ISO string or Date object
       * @returns School wall-clock hour
       */
      getHour: (dateString: string | Date): number => {
        if (!dateString) return 0;
        try {
          // If it's a naive string (no Z, no +/- offset), we treat it as school local time directly
          if (typeof dateString === 'string' && !dateString.includes('Z') && !dateString.includes('+')) {
            // ISO-8601 wall clock string like 2024-03-29T12:00
            // We just extract the hour from the string directly to be bulletproof
            const match = dateString.match(/T(\d{2})/);
            if (match) return parseInt(match[1], 10);
            
            // Fallback for date-only strings or malformed
            return 0;
          }

          // Otherwise, parse and convert to school time
          const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
          const schoolDate = toZonedTime(date, schoolTimezone);
          return schoolDate.getHours();
        } catch (e) {
          console.error('Error getting hour:', e);
          return 0;
        }
      },

      /**
       * Get current date in school timezone as ISO date string (YYYY-MM-DD)
       * @returns Current date in school timezone as ISO date string
       */
      getSchoolDateISO: (): string => {
        const schoolDate = toZonedTime(new Date(), schoolTimezone);
        return format(schoolDate, 'yyyy-MM-dd');
      },

      /**
       * Get first day of current month in school timezone as ISO date string
       * @returns First day of current month in school timezone as ISO date string
       */
      getFirstDayOfMonthISO: (): string => {
        const schoolDate = toZonedTime(new Date(), schoolTimezone);
        const firstDay = new Date(
          schoolDate.getFullYear(),
          schoolDate.getMonth(),
          1
        );
        return format(firstDay, 'yyyy-MM-dd');
      },

      /**
       * Convert any date to ISO date string in school timezone
       * @param date - Date to convert
       * @returns ISO date string (YYYY-MM-DD) in school timezone
       */
      toSchoolDateISO: (date: string | Date): string => {
        const dateObj = typeof date === 'string' ? parseISO(date) : date;
        return formatInTimeZone(dateObj, schoolTimezone, 'yyyy-MM-dd');
      },

      /**
       * Get first day of current month in school timezone as ISO date string
       * @returns First day of current month in school timezone as ISO date string
       */
      compareDates: (date1: string | Date, date2: string | Date): number => {
        const d1 = typeof date1 === 'string' ? parseISO(date1) : date1;
        const d2 = typeof date2 === 'string' ? parseISO(date2) : date2;

        const schoolD1 = toZonedTime(d1, schoolTimezone);
        const schoolD2 = toZonedTime(d2, schoolTimezone);

        if (schoolD1 < schoolD2) return -1;
        if (schoolD1 > schoolD2) return 1;
        return 0;
      },

      /**
       * Convert a local date to school timezone
       * @param date - Date object in local timezone
       * @returns Date object converted to school timezone
       */
      toSchoolTimezone: (date: Date): Date => {
        return toZonedTime(date, schoolTimezone);
      },

      /**
       * Convert a school timezone date to UTC
       * @param date - Date object in school timezone
       * @returns Date object converted to UTC
       */
      fromSchoolTimezone: (date: Date): Date => {
        return fromZonedTime(date, schoolTimezone);
      },

      /**
       * Get the school's timezone identifier
       * @returns Timezone string (e.g., 'Africa/Accra')
       */
      getTimezone: (): string => {
        return schoolTimezone;
      },

      /**
       * Validate if a date string or object is valid
       * @param dateString - Date string or Date object to validate
       * @returns True if valid, false otherwise
       */
      isValidDate: (dateString: string | Date): boolean => {
        if (!dateString) return false;

        if (dateString instanceof Date) {
          return isValid(dateString);
        } else {
          const date = parseISO(dateString);
          return isValid(date);
        }
      },
    };
  }, [instance?.timezone]);

  return {
    instance,
    loading,
    dateUtils,
    isRunningOnMobile,
    DATE_FORMATS,
  };
}
